// Components==============
import storeReflection from 'actions/reflection/store';
import updateReflection from 'actions/reflection/update';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { ReflectionSaveState } from 'typings';
import { getInitialReflectionSaveState } from 'utilities/getInitialReflectionSaveState';
import { ReflectContextType } from './Reflect';
// =========================

export type ReflectFunctions = ReturnType<typeof useReflectFunctions>;

export function useReflectFunctions({
  endPointActivities,
  endPointActivity,
  setUpdateCounter,
  setDispatchingDreams,
}: ReflectContextType) {
  const dispatch = useReduxAction();

  const userId = useRedux((state) => state.authentication?.user?.id);

  const reflections = useRedux((state) =>
    state.reflection?.entities.allIds
      .map((id) => state.reflection?.entities.byId[id])
      .filter((r) => r && r.created_by === userId)
  );

  function handleSave(
    saveState: ReflectionSaveState,
    setSaveState: React.Dispatch<React.SetStateAction<ReflectionSaveState>>
  ) {
    if (saveState.id) return dispatch(updateReflection(saveState));
    dispatch(storeReflection(saveState)).then((result: ReflectionEntity) =>
      setSaveState({ ...saveState, id: result.id })
    );
  }

  function mapEmotions(
    emotions: EmotionsEntity[]
  ): { id: number; description: string }[] {
    return emotions.map((emotion) => ({
      id: emotion.id,
      description: emotion.pivot.description,
    }));
  }

  function handleSaveReflection(updatedReflections: ReflectionEntity[]) {
    // take in updated reflections that have the new sparks added to them
    updatedReflections.forEach((reflection) => {
      const { id, sparks, emotions } = reflection;
      const sparkIds = sparks.map((spark) => spark.id);
      const mappedEmotions = mapEmotions(emotions);

      // updateReflection expects a ReflectionSaveState so we need to map the data
      const reflectionSaveState: ReflectionSaveState = {
        id,
        result: reflection.result,
        contributed: reflection.contributed,
        finished: reflection.finished,
        project_process_id: reflection.project_process_id,
        // reflection_on_student: reflection.reflection_on_student,
        note: reflection.note,
        emotions: mappedEmotions,
        sparks: sparkIds,
        dreams: reflection.dreams.map((dream) => dream.id),
      };

      // dispatch the updated reflection
      dispatch(updateReflection(reflectionSaveState));
    });
  }

  function handleContributeAllDreams(dreams: number[]) {
    // set loading to true
    setDispatchingDreams(true);

    // Dreams that were already selected in the end reflection
    const previousDreams = reflections
      ?.find((r) => r?.project_process_id === endPointActivity?.id)
      ?.dreams.map((d) => d.id);

    // Dreams that were removed in the current dispatch
    const deletedDreams = previousDreams?.filter((d) => !dreams.includes(d));

    const promises = [...endPointActivities, endPointActivity].map(
      (activity) => {
        if (!activity) return;

        const existingReflection = reflections?.find(
          (r) => r?.project_process_id === activity?.id
        );

        // if existing reflection is undefined it will also serve as a new saveState for a dream
        const saveState = getInitialReflectionSaveState(
          activity.id,
          existingReflection
        );

        if (existingReflection) {
          // cancel dispatch if there are no changes
          const hasChanges =
            dreams.find((d) => !saveState.dreams.includes(d)) ||
            deletedDreams?.find((d) => saveState.dreams.includes(d));

          if (!hasChanges) return;

          // Remove duplicate dreams
          const filteredDreams = saveState.dreams.filter(
            (d) => !dreams.includes(d)
          );

          // Add new dreams
          const newDreamValue = [...filteredDreams, ...dreams].filter(
            (d) => !deletedDreams?.includes(d)
          );

          // get new value
          const newValue = { ...saveState, dreams: newDreamValue };

          return dispatch(updateReflection(newValue));
        }

        return dispatch(storeReflection({ ...saveState, dreams }));
      }
    );

    Promise.all(promises)
      .then(() => {
        // set loading to false
        setDispatchingDreams(false);
        // update react state
        setUpdateCounter((prev) => prev + 1);
      })
      .catch(() => setDispatchingDreams(false));
  }

  function handleInvitePeer(userIds: number[], processId: number) {
    return userIds.forEach((user_id) => {
      const saveState: ReflectionSaveState & { user_id: number } = {
        id: 0,
        user_id,
        result: 0,
        contributed: false,
        finished: false,
        project_process_id: processId,
        reflection_on_student: userId,
        note: [],
        emotions: [],
        sparks: [],
        dreams: [],
      };

      dispatch(storeReflection(saveState));
    });
  }

  return {
    handleSave,
    handleInvitePeer,
    handleContributeAllDreams,
    handleSaveReflection,
  };
}
